import React, { Suspense, useState, useEffect, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Link } from 'react-router-dom';
import { Github, Linkedin, Menu, X, GraduationCap, Award, BookOpen, Camera, Code2, Palette } from 'lucide-react';
import ScrollDown from '../components/ui/ScrollDown';
import ParticleCursor from '../components/ui/ParticleCursor';
import SkillsAndExpertise from '../components/home/SkillsAndExpertise';
import SEOHelmet from '../components/SEO/SEOHelmet';

const Photography = React.lazy(() => import('../pages/Photography'));
const Projects = React.lazy(() => import('../pages/Projects'));
const Designing = React.lazy(() => import('../pages/Designing'));
const Blog = React.lazy(() => import('../pages/Blog'));
const BlogDetail = React.lazy(() => import('../pages/BlogDetail'));

type NavItem =
  | { label: string; type: 'section'; target: string }
  | { label: string; type: 'route'; to: string };

const MyraWidget = React.lazy(() => import('../components/myra/MyraWidget'));

function MainContent() {
  const location = useLocation();
  
  // If we're on a page route, don't render the main app content
  if (location.pathname !== '/') {
    return null;
  }
   
  return <HomePage />;
}

function HomePage() {
  const [activeSection, setActiveSection] = useState('home');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  // SEO Meta Tags for Home Page
  const homePageSEO = {
    title: 'Vijay Thumma – Java Full Stack Developer | Spring Boot & React',
    description: 'Full Stack Developer with 8+ years of experience specializing in Java, Spring Boot, React, and Microservices. Expert in building scalable cloud-native applications with AWS, Docker, Kubernetes, and modern development practices.',
    keywords: 'Vijay Thumma, Java Full Stack Developer, Spring Boot Developer, React Developer, Software Engineer, Full Stack Engineer, Backend Developer, Frontend Developer, Spring Boot Microservices Developer, REST API Developer, AWS Cloud Engineer, Microservices Architecture, Docker and Kubernetes Developer, Java 11 Developer, Java 17 Developer, J2EE and Hibernate Specialist, PostgreSQL and MongoDB Expert, CI/CD Jenkins Developer, Agile Software Engineer, Cloud-Native Application Developer, Full Stack Application Development',
    canonical: 'https://www.vthumma.com/',
    ogUrl: 'https://www.vthumma.com/',
  };
  
  // Memoize nav items to prevent recreation on every render
  const navItems = useMemo<NavItem[]>(
    () => [
      { label: 'Home', type: 'section', target: 'home' },
      { label: 'About', type: 'section', target: 'about' },
      { label: 'Skills', type: 'section', target: 'skills' },
      { label: 'Hobbies', type: 'section', target: 'hobbies' },
      { label: 'Blog', type: 'route', to: '/blog' },
      { label: 'Contact', type: 'section', target: 'contact' },
    ],
    []
  );

  const sectionNavItems = useMemo(
    () => navItems.filter((item): item is Extract<NavItem, { type: 'section' }> => item.type === 'section'),
    [navItems]
  );

  // Memoize the sections array to prevent recreation
  const sections = useMemo(
    () => sectionNavItems.map((item) => item.target),
    [sectionNavItems]
  );

  // Handle scroll events for navbar and section highlighting
  useEffect(() => {
    const handleScroll = () => {
      // Check if scrolled past 50px for navbar style change
      const scrolled = window.scrollY > 50;
      if (scrolled !== isScrolled) {
        setIsScrolled(scrolled);
      }
      
      // Update active section based on scroll position
      const scrollPosition = window.scrollY + 100;
      let newActiveSection = activeSection;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            newActiveSection = section;
            break;
          }
        }
      }
      
      if (newActiveSection !== activeSection) {
        setActiveSection(newActiveSection);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Call once to set initial state

    return () => window.removeEventListener('scroll', handleScroll);
  }, [sections, activeSection, isScrolled]);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
      setIsMenuOpen(false);
    }
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormStatus('sending');

    // Allow overriding the endpoint via env (e.g., REACT_APP_FORMSPREE_ENDPOINT)
    const endpoint = (process.env.REACT_APP_FORMSPREE_ENDPOINT || 'https://formspree.io/f/movpgjwr').trim();

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
          _replyto: formData.email,
          _subject: `Portfolio Contact: ${formData.subject}`
        })
      });

      if (response.ok) {
        setFormStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
        setTimeout(() => setFormStatus('idle'), 5000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 5000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 5000);
    }
  };

  // Experience data
  const experiences = [
    {
  title: 'Java Full Stack Developer',
  company: 'Wells Fargo',
  location: 'Phoenix, Arizona (Hybrid)',
  period: 'Sep 2025 - Present',
  description: 'Working as a Java Full Stack Developer responsible for designing, developing, and maintaining scalable, secure microservices in an Agile environment.',
  achievements: [
    'Designed and developed scalable microservices using Java 17, Spring Boot, and Spring Cloud components',
    'Built RESTful APIs with OpenAPI/Swagger following proper HTTP standards and best practices',
    'Developed responsive front-end applications using ReactJS, React Hooks, Redux, and React Router',
    'Implemented secure authentication and authorization using Spring Security and JWT',
    'Automated CI/CD pipelines using Jenkins, OpenShift, and Harness for seamless deployments',
    'Optimized application performance through multithreading, database indexing, and query tuning',
    'Collaborated in Agile Scrum teams, actively participating in sprint planning, reviews, and code reviews'
  ]
}
,
    {
  title: 'Full Stack Java Developer',
  company: 'JPMC',
  location: 'Columbus, Ohio',
  period: 'Aug 2024 - Sep 2025',
  description: 'Delivered end-to-end solutions across all SDLC phases within Agile Scrum teams for enterprise-scale banking applications.',
  achievements: [
    'Architected and maintained microservices using Spring Boot, Spring Cloud Config, API Gateway, and Feign Clients',
    'Designed event-driven systems using Apache Kafka and Kafka Connect for real-time data integration',
    'Modernized legacy applications using Java 21 and Spring Boot with cloud-native patterns',
    'Migrated applications from on-premise to AWS ECS, Fargate, RDS/Aurora PostgreSQL using Docker and Kubernetes',
    'Enhanced system resilience using Circuit Breaker patterns (Hystrix) and fault-tolerant design',
    'Built batch and scheduled jobs using Spring Batch and Spring Scheduler',
    'Implemented CI/CD pipelines using Jenkins, GitLab CI, AWS CodePipeline, and Spinnaker',
    'Monitored system health using Grafana, Prometheus, and Splunk to improve reliability',
  ]
}
,
    {
  title: 'Software Engineer',
  company: 'VISAT AST LLC',
  location: 'Youngstown, Ohio',
  period: 'Jan 2022 - Dec 2023',
  description: 'Developed industrial automation solutions using Java and robotics technologies.',
  achievements: [
    'Programmed Java-based robotic arm automation achieving high accuracy in object placement',
    'Implemented color detection and object sorting using computer vision techniques',
    'Calibrated robotic systems to ensure precise motion control and reliability',
    'Collaborated with hardware and software teams to integrate sensors and optimize workflows',
    'Enhanced control interface UX for better operator usability'
  ]
}
,
    {
  title: 'Java Developer',
  company: 'Publicis Sapient',
  location: 'Bangalore, India',
  period: 'Aug 2015 - Jun 2018',
  description: 'Led full-stack development of enterprise and e-commerce applications using Java, Spring, and cloud technologies.',
  achievements: [
    'Designed and developed RESTful APIs using Spring Boot and integrated Angular front-end applications',
    'Applied design patterns (Singleton, DAO, MVC) to build scalable and maintainable systems',
    'Implemented security using Spring Security with OAuth2 and JWT',
    'Enabled real-time messaging using Apache Kafka and optimized MongoDB schemas',
    'Containerized applications using Docker and deployed via Kubernetes',
    'Integrated CI/CD pipelines using Jenkins and Maven',
    'Managed AWS infrastructure including EC2, S3, ELB, and RDS',
    'Enforced code quality through SonarQube, JUnit, and Mockito'
  ]
},
    {
  title: 'Software Engineer',
  company: 'Publicis Sapient',
  location: 'Bangalore, India',
  period: 'Feb 2013 - Mar 2015',
  description: 'Developed high-performance enterprise applications using Core Java and Spring frameworks.',
  achievements: [
    'Built RESTful web services using Spring MVC and Spring Boot',
    'Integrated SOAP services with legacy enterprise systems',
    'Optimized MySQL databases using Hibernate ORM and query tuning',
    'Implemented secure payment gateway integrations and transaction workflows',
    'Developed responsive UI components using HTML, CSS, and JavaScript',
    'Automated builds and dependency management using Maven',
    'Implemented centralized logging using Log4j',
    'Deployed applications on Azure using Azure App Service, AKS, and Azure DevOps pipelines'
  ]
}

  ];

  // Education data
  const education = [
    {
      degree: "Master's in Computing and Information Systems",
      institution: 'Youngstown State University',
      period: '2022 - 2023'
    },
    {
      degree: "M.B.A in Information Technology",
      institution: 'DIGITAL COLLEGE, Paris, France',
      period: '2018 - 2020'
    },
    {
      degree: "Bachelor's in Multimedia and Animation",
      institution: 'SMU',
      period: '2010 - 2013'
    }
  ];

  // Hobbies data with links
  const hobbies = [
    {
      title: 'Photography',
      description: 'Capturing moments and landscapes with a passion for visual storytelling',
      technologies: ['Landscape', 'Portrait', 'Street', 'Editing'],
      link: '/photography'
    },
    {
      title: 'Projects',
      description: 'Exploring various personal interests and creative pursuits',
      technologies: ['Web Apps', 'APIs', 'Open Source', 'Automation'],
      link: '/projects'
    },
    {
      title: 'Designing',
      description: 'Creating beautiful and functional user interfaces and experiences',
      technologies: ['UI/UX', 'Wireframing', 'Prototyping', 'Figma'],
      link: '/designing'
    }
  ];

  return (
    <div className="min-h-screen text-slate-100">
      <SEOHelmet {...homePageSEO} />
      <ParticleCursor />
      {/* Navigation */}
      <nav className={`fixed w-full z-50 transition-all duration-300 ${isScrolled ? 'bg-[#020617]/90 backdrop-blur-md py-3 shadow-lg' : 'py-5'}`}>
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex h-12 items-center justify-between">
            <button 
              onClick={() => scrollToSection('home')}
              className="focus:outline-none shrink-0"
            >
              <picture>
                <source
                  type="image/webp"
                  srcSet="/images/small/VJ.webp 400w, /images/medium/VJ.webp 800w, /images/original/VJ.webp 1200w"
                  sizes="64px"
                />
                <img
                  src="/images/VJ.png"
                  alt="Vijay Sagar Logo"
                  className="h-10 md:h-12 w-auto object-contain"
                  width="64"
                  height="64"
                  loading="eager"
                  decoding="async"
                  style={{
                    contentVisibility: 'visible'
                  }}
                />
              </picture>
            </button>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8 ml-6">
              {navItems.map((item) =>
                item.type === 'section' ? (
                  <button
                    key={item.target}
                    onClick={() => scrollToSection(item.target)}
                    className={`relative px-2 py-1 text-sm font-medium transition-colors ${
                      activeSection === item.target
                        ? 'text-[rgb(239,171,10)]'
                        : 'text-gray-300 hover:text-[rgb(239,171,10)]'
                    }`}
                  >
                    {item.label}
                    {activeSection === item.target && (
                      <span className="absolute bottom-0 left-0 w-full h-0.5 bg-[rgb(239,171,10)]"></span>
                    )}
                  </button>
                ) : (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="relative px-2 py-1 text-sm font-medium text-gray-300 transition-colors hover:text-[rgb(239,171,10)]"
                  >
                    {item.label}
                  </Link>
                )
              )}
            </div>

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 rounded-md text-gray-300 hover:text-[rgb(239,171,10)] focus:outline-none"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="md:hidden mt-4 pb-4 space-y-2">
              {navItems.map((item) =>
                item.type === 'section' ? (
                  <button
                    key={item.target}
                    onClick={() => scrollToSection(item.target)}
                    className={`block w-full text-left px-3 py-2 rounded-md text-sm font-medium ${
                      activeSection === item.target
                        ? 'bg-gray-800 text-[rgb(239,171,10)]'
                        : 'text-gray-300 hover:bg-gray-800 hover:text-[rgb(239,171,10)]'
                    }`}
                  >
                    {item.label}
                  </button>
                ) : (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setIsMenuOpen(false)}
                    className="block w-full text-left px-3 py-2 rounded-md text-sm font-medium text-gray-300 hover:bg-gray-800 hover:text-[rgb(239,171,10)]"
                  >
                    {item.label}
                  </Link>
                )
              )}
            </div>
          )}
        </div>
      </nav>

      <main>
        {/* Hero Section */}
        <section id="home" className="min-h-screen flex items-center justify-center relative overflow-hidden pt-20" style={{ backgroundColor: 'rgb(2, 6, 23)' }}>
          <div className="absolute inset-0 opacity-5 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
          <div className="container mx-auto px-4 md:px-12 relative z-10 py-12">
            <div className="flex flex-col lg:flex-row-reverse items-center justify-center gap-8">
              <div className="text-center lg:text-right max-w-xl mx-auto lg:mr-8 lg:ml-0">
                <div className="inline-block px-4 py-1 mb-6 text-sm font-medium text-[rgb(239,171,10)] bg-[rgb(239,171,10)]/10 rounded-full border border-[rgb(239,171,10)]/30">
                  Full Stack Developer
                </div>
                
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                  Hi, I'm <span className="text-[rgb(239,171,10)]">Vijay Sagar Thumma</span>
                </h1>
                
                <h2 className="text-xl md:text-2xl font-medium text-slate-300 mb-6">
                  Java Full Stack Developer
                </h2>
                
                <p className="text-lg text-slate-400 mb-8 leading-relaxed">
                I specialize in building robust, scalable, and user-friendly applications by combining the power of Java & Spring Boot on the backend with React & modern JavaScript on the frontend. From designing APIs to crafting intuitive UIs, I bring end-to-end full-stack solutions that turn complex ideas into reality.
                </p>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-end">
                  <button 
                    onClick={() => scrollToSection('contact')}
                    className="px-8 py-3 bg-gradient-to-r from-[rgb(239,171,10)] to-[rgb(255,200,50)] text-gray-900 font-medium rounded-lg transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg hover:shadow-[rgb(239,171,10)]/25"
                  >
                    Get In Touch
                  </button>
                  <button 
                    onClick={() => scrollToSection('hobbies')}
                    className="px-8 py-3 border border-[rgb(239,171,10)] text-[rgb(239,171,10)] hover:bg-[rgb(239,171,10)]/10 font-medium rounded-lg transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg hover:shadow-[rgb(239,171,10)]/15"
                  >
                    View My Work
                  </button>
                </div>
              </div>
              
              <div className="relative w-full max-w-md mx-auto lg:mx-0">
                <div className="relative z-10 w-full aspect-auto rounded-2xl overflow-hidden border-4 border-[rgb(239,171,10)]/20 shadow-2xl bg-slate-700/50">
                  <picture>
                    <source
                      type="image/webp"
                      srcSet="/images/small/about-pic.webp 400w, /images/medium/about-pic.webp 800w, /images/large/about-pic.webp 1200w"
                      sizes="(max-width: 1024px) 90vw, 28rem"
                    />
                    <img
                      src="/images/about-pic.png"
                      alt="Vijay Sagar - Full Stack Developer"
                      className="w-full h-auto object-contain"
                      width="800"
                      height="800"
                      loading="eager"
                      decoding="async"
                      fetchPriority="high"
                      style={{
                        contentVisibility: 'auto',
                        maxHeight: '600px'
                      }}
                      onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => {
                        const img = e.currentTarget;
                        img.onerror = null;
                        img.src = 'https://via.placeholder.com/600x600/1e3a8a/ffffff?text=VS';
                      }}
                    />
                  </picture>
                </div>
                <div className="absolute -z-10 top-6 -right-6 w-full h-full rounded-2xl border-2 border-[rgb(239,171,10)]/20"></div>
              </div>
            </div>
          </div>
          <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2">
            <ScrollDown targetId="about" color="rgb(239,171,10)" size={56} />
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="relative py-20 overflow-hidden" style={{ backgroundColor: 'rgb(2, 6, 23)' }}>
          <div className="absolute inset-0 opacity-5 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
          <div className="relative container mx-auto px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-16">
                <span className="inline-block mb-4 text-sm font-semibold text-blue-400 uppercase tracking-wider">Get To Know Me</span>
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                  About <span className="text-[rgb(239,171,10)]">Me</span>
                </h2>
                <div className="w-20 h-1 bg-gradient-to-r from-[rgb(239,171,10)] to-[rgb(255,200,50)] mx-auto"></div>
              </div>
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div>
                  <h3 className="text-2xl font-semibold mb-4">Who am I?</h3>
                  <p className="text-slate-300 mb-6">
                    I'm a passionate Full Stack Java Developer with expertise in building scalable web applications using Java, Spring Boot, and modern JavaScript frameworks. With a strong foundation in computer science and years of hands-on experience, I specialize in creating efficient, maintainable, and user-friendly solutions.
                  </p>
                  <p className="text-slate-300 mb-6">
                    My journey in software development started with a curiosity for problem-solving and has evolved into a career where I've had the opportunity to work with talented teams on challenging projects. I'm always eager to learn new technologies and methodologies to stay at the forefront of web development.
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <button
                      onClick={() => {
                        // Create a temporary anchor element
                        const link = document.createElement('a');
                        link.href = '/images/VIJAY_T.pdf';
                        link.download = 'VIJAY_T.pdf';
                        // Append to the document
                        document.body.appendChild(link);
                        // Trigger the download
                        link.click();
                        // Clean up
                        document.body.removeChild(link);
                      }}
                      className="px-6 py-2 border border-[rgb(239,171,10)] text-[rgb(239,171,10)] hover:bg-[rgb(239,171,10)]/10 font-medium rounded-md transition-colors"
                    >
                      Download CV
                    </button>
                  </div>
                </div>
                <div className="relative">
                  <div className="relative z-10 w-64 h-64 mx-auto rounded-full overflow-hidden border-4 border-blue-500/20">
                    <div className="w-full h-full bg-slate-700 flex items-center justify-center">
                      <span className="text-4xl font-bold text-slate-400">VS</span>
                    </div>
                  </div>
                  <div className="absolute -bottom-4 -right-4 w-64 h-64 rounded-full border-2 border-blue-500/30"></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Skills & Expertise Section */}
        <SkillsAndExpertise />

        {/* Experience Section */}
        <section id="experience" className="relative py-20 overflow-hidden" style={{ backgroundColor: 'rgb(2, 6, 23)' }}>
          <div className="absolute inset-0 opacity-5 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
          <div className="relative container mx-auto px-4 md:px-6">
            <div className="text-center mb-16">
              <span className="inline-block mb-4 text-sm font-semibold text-blue-400 uppercase tracking-wider">My Professional Journey</span>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Work <span className="text-[rgb(239,171,10)]">Experience</span>
              </h2>
              <div className="w-20 h-1 bg-gradient-to-r from-[rgb(239,171,10)] to-[rgb(255,200,50)] mx-auto"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto perspective-1000">
              {experiences.map((exp, index) => (
                <div 
                  key={index}
                  className="group h-[32rem] [transform-style:preserve-3d] transition-all duration-500 ease-in-out hover:rotate-y-180 relative"
                >
                  {/* Front of the cube */}
                  <div className="absolute inset-0 bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-6 shadow-2xl hover:shadow-2xl hover:shadow-[rgb(239,171,10)]/15 transition-all duration-500 backface-hidden [backface-visibility:hidden] flex flex-col overflow-hidden">
                    {/* Decorative elements */}
                    <div className="absolute -top-4 -right-4 w-20 h-20 bg-[rgb(239,171,10)]/5 rounded-full"></div>
                    <div className="absolute -bottom-6 -left-6 w-28 h-28 bg-blue-500/5 rounded-full"></div>
                    
                    <div className="relative z-10 h-full flex flex-col space-y-4">
                      {/* Header Section */}
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="text-xl font-bold text-white group-hover:text-[rgb(239,171,10)] transition-colors duration-300">
                            {exp.title}
                          </h3>
                          <p className="text-slate-300 text-base font-medium">{exp.company}</p>
                        </div>
                        <span className="px-3 py-1 text-xs font-medium bg-blue-900/30 text-blue-300 rounded-full whitespace-nowrap shadow-md">
                          {exp.period}
                        </span>
                      </div>
                    
                      {/* Location */}
                      {exp.location && (
                        <div className="flex items-center text-slate-400 text-sm -mt-1">
                          <svg className="w-4 h-4 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                          </svg>
                          {exp.location}
                        </div>
                      )}
                      
                      {/* Divider */}
                      <div className="h-px bg-gradient-to-r from-transparent via-slate-700 to-transparent my-2"></div>
                      
                      {/* Description */}
                      <p className="text-slate-300 text-sm leading-relaxed line-clamp-2">{exp.description}</p>
                      
                      {/* Key Achievements */}
                      <div className="flex-1 overflow-y-auto pr-2">
                        
                        <ul className="space-y-2.5">
                          {exp.achievements.map((achievement, i) => (
                            <li key={i} className="flex items-start group/achievement">
                              <span className="text-[rgb(239,171,10)] text-lg leading-none mr-2 mt-0.5">•</span>
                              <span className="text-sm text-slate-300 leading-relaxed group-hover/achievement:text-white transition-colors">
                                {achievement}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    
                  </div>
                  
                  </div>
                  
                  {/* Back of the cube */}
                  <div className="absolute inset-0 bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl p-8 shadow-2xl backface-hidden [backface-visibility:hidden] [transform:rotateY(180deg)] flex flex-col justify-center items-center z-10">
                    <div className="absolute inset-0 opacity-5 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMzAgMTVjLTguMjg0IDAtMTUgNi43MTYtMTUgMTVzNi43MTYgMTUgMTUgMTUgMTUtNi43MTYgMTUtMTUtNi43MTYtMTUtMTUtMTV6bTAgMjRjLTQuOTcxIDAtOS00LjAyOS05LTlzNC4wMjktOSA5LTkgOSA0LjAyOSA5IDktNC4wMjkgOS05IDl6IiBmaWxsPSIjZWY5YjBhIi8+PC9zdmc+')]"></div>
                    <div className="text-center relative z-10">
                      <h3 className="text-2xl font-bold text-white mb-4">{exp.company}</h3>
                      <div className="w-24 h-1 bg-gradient-to-r from-[rgb(239,171,10)] to-[rgb(255,200,50)] mx-auto mb-8 rounded-full"></div>
                      <p className="text-slate-300 mb-6 text-sm uppercase tracking-wider">Key Technologies</p>
                      <div className="grid grid-cols-2 gap-3 mb-8 max-w-xs mx-auto">
                        {['Java', 'Spring Boot', 'AWS', 'React', 'Docker', 'Kubernetes'].map((tech, i) => (
                          <span 
                            key={i} 
                            className="px-3 py-2 text-xs font-medium bg-slate-800/80 text-slate-200 rounded-lg shadow-md hover:shadow-lg
                              hover:bg-gradient-to-r hover:from-[rgb(239,171,10)] hover:to-[rgb(255,200,50)] hover:text-slate-900 transition-all duration-300 transform hover:scale-105"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                      <button 
                        className="px-6 py-2.5 bg-gradient-to-r from-[rgb(239,171,10)] to-[rgb(255,200,50)] text-slate-900 font-medium rounded-lg hover:opacity-90 transition-all duration-300 shadow-md hover:shadow-lg hover:shadow-[rgb(239,171,10)]/25"
                        onClick={(e) => {
                          e.stopPropagation();
                          // Add your click handler here
                        }}
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                  
                  {/* Cube sides */}
                  <div className="absolute inset-0 border-2 border-[rgb(239,171,10)]/20 rounded-xl pointer-events-none"></div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-xl"></div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Education Section */}
        <section id="education" className="relative py-20 overflow-hidden" style={{ backgroundColor: 'rgb(2, 6, 23)' }}>
          <div className="absolute inset-0 opacity-5 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
          <div className="relative container mx-auto px-4 md:px-6">
            <div className="text-center mb-16">
              <span className="inline-block mb-4 text-sm font-semibold text-blue-400 uppercase tracking-wider">My Journey</span>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                Education & <span className="text-[rgb(239,171,10)]">Background</span>
              </h2>
              <div className="w-20 h-1 bg-gradient-to-r from-[rgb(239,171,10)] to-[rgb(255,200,50)] mx-auto"></div>
            </div>

            <div className="grid md:grid-cols-3 gap-8 relative z-10">
              {education.map((edu, index) => {
                const icons = [<GraduationCap className="w-8 h-8" />, <Award className="w-8 h-8" />, <BookOpen className="w-8 h-8" />];
                return (
                  <div 
                    key={index} 
                    className="group relative bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-8 shadow-2xl hover:shadow-2xl hover:shadow-[rgb(239,171,10)]/15 transition-all duration-500 hover:-translate-y-2 overflow-hidden"
                  >
                    <div className="absolute -top-4 -right-4 w-24 h-24 bg-[rgb(239,171,10)]/5 rounded-full"></div>
                    <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-blue-500/5 rounded-full"></div>
                    
                    <div className="relative z-10">
                      <div className="w-16 h-16 mb-6 rounded-xl bg-gradient-to-br from-[rgb(239,171,10)] to-yellow-500 flex items-center justify-center text-white shadow-lg">
                        {React.cloneElement(icons[index], { className: 'w-8 h-8' })}
                      </div>
                      
                      <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[rgb(239,171,10)] transition-colors">
                        {edu.degree}
                      </h3>
                      
                      <p className="text-slate-300 mb-4 flex items-center">
                        <svg className="w-4 h-4 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5m9 0v-5c0-1.1-.9-2-2-2H7a2 2 0 00-2 2v5"></path>
                        </svg>
                        {edu.institution}
                      </p>
                      
                      <div className="flex items-center text-sm text-blue-400">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                        </svg>
                        {edu.period}
                      </div>
                    </div>
                    
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-xl"></div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Hobbies Section */}
        <section id="hobbies" className="relative py-20 overflow-hidden" style={{ backgroundColor: 'rgb(2, 6, 23)' }}>
          <div className="absolute inset-0 opacity-5 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
          <div className="relative container mx-auto px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-16">
                <span className="inline-block mb-4 text-sm font-semibold text-blue-400 uppercase tracking-wider">My Interests</span>
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                  My <span className="text-[rgb(239,171,10)]">Hobbies</span>
                </h2>
                <div className="w-20 h-1 bg-gradient-to-r from-[rgb(239,171,10)] to-[rgb(255,200,50)] mx-auto"></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative z-10">
                {hobbies.map((hobby, index) => {
                  const icons = [<Camera className="w-8 h-8" />, <Code2 className="w-8 h-8" />, <Palette className="w-8 h-8" />];
                  return (
                    <a 
                      key={index} 
                      href={hobby.link}
                      className="group relative bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl p-8 shadow-2xl hover:shadow-2xl hover:shadow-[rgb(239,171,10)]/20 transition-all duration-500 hover:-translate-y-2 overflow-hidden block"
                    >
                      <div className="absolute -top-4 -right-4 w-24 h-24 bg-[rgb(239,171,10)]/5 rounded-full"></div>
                      <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-blue-500/5 rounded-full"></div>
                      
                      <div className="relative z-10">
                        <div className="w-16 h-16 mb-6 rounded-xl bg-gradient-to-br from-[rgb(239,171,10)] to-yellow-500 flex items-center justify-center text-white shadow-lg">
                          {React.cloneElement(icons[index], { className: 'w-8 h-8' })}
                        </div>
                        
                        <h3 className="text-xl font-bold text-white mb-3 group-hover:text-[rgb(239,171,10)] transition-colors">
                          {hobby.title}
                        </h3>
                        
                        <p className="text-slate-300 mb-6">{hobby.description}</p>
                        
                        <div className="flex flex-wrap gap-2">
                          {hobby.technologies.map((tech, i) => (
                            <span key={i} className="px-3 py-1 bg-slate-700/50 text-slate-300 text-xs rounded-full">
                              {tech}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity rounded-xl"></div>
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="relative py-20 overflow-hidden" style={{ backgroundColor: 'rgb(2, 6, 23)' }}>
          <div className="absolute inset-0 opacity-5 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
          <div className="relative container mx-auto px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-16">
                <span className="inline-block mb-4 text-sm font-semibold text-blue-400 uppercase tracking-wider">Get In Touch</span>
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                  Contact <span className="text-[rgb(239,171,10)]">Me</span>
                </h2>
                <div className="w-20 h-1 bg-gradient-to-r from-[rgb(239,171,10)] to-[rgb(255,200,50)] mx-auto"></div>
              </div>
              <div className="grid md:grid-cols-2 gap-12">
                <div>
                  <h3 className="text-2xl font-semibold mb-6">Let's Connect</h3>
                  <p className="text-slate-400 mb-8 leading-relaxed">
                    I'm currently open to new opportunities and collaborations. Whether you have a question, want to discuss a project, or just want to say hi, feel free to reach out. I'll get back to you as soon as possible!
                  </p>
                  
                  <div className="bg-slate-800/50 rounded-xl p-6 mb-8">
                    <h4 className="text-lg font-semibold mb-4 text-[rgb(239,171,10)]">Why Work With Me?</h4>
                    <ul className="space-y-3 text-slate-300">
                      <li className="flex items-start">
                        <span className="text-[rgb(239,171,10)] mr-2">✓</span>
                        <span>8+ years of full-stack development experience</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[rgb(239,171,10)] mr-2">✓</span>
                        <span>Expertise in Java, Spring Boot, React & Cloud technologies</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[rgb(239,171,10)] mr-2">✓</span>
                        <span>Strong focus on scalable & secure solutions</span>
                      </li>
                      <li className="flex items-start">
                        <span className="text-[rgb(239,171,10)] mr-2">✓</span>
                        <span>Collaborative team player with Agile experience</span>
                      </li>
                    </ul>
                  </div>

                  <div className="flex space-x-4">
                    <a 
                      href="https://github.com/thummasreddy" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="p-3 bg-slate-700 rounded-lg hover:bg-[rgb(239,171,10)] hover:text-gray-900 transition-colors"
                      aria-label="GitHub Profile"
                    >
                      <Github className="w-6 h-6" />
                    </a>
                    <a 
                      href="https://www.linkedin.com/in/vsagarthumma/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="p-3 bg-slate-700 rounded-lg hover:bg-[rgb(239,171,10)] hover:text-gray-900 transition-colors"
                      aria-label="LinkedIn Profile"
                    >
                      <Linkedin className="w-6 h-6" />
                    </a>
                  </div>
                </div>

                <form className="space-y-6" onSubmit={handleFormSubmit}>
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium mb-2">Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleFormChange}
                      className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg focus:ring-2 focus:ring-[rgb(239,171,10)] focus:border-transparent text-white"
                      placeholder="Your name"
                      required
                      disabled={formStatus === 'sending'}
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium mb-2">Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleFormChange}
                      className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg focus:ring-2 focus:ring-[rgb(239,171,10)] focus:border-transparent text-white"
                      placeholder="your.email@example.com"
                      required
                      disabled={formStatus === 'sending'}
                    />
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium mb-2">Subject *</label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleFormChange}
                      className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg focus:ring-2 focus:ring-[rgb(239,171,10)] focus:border-transparent text-white"
                      placeholder="How can I help you?"
                      required
                      disabled={formStatus === 'sending'}
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium mb-2">Message *</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleFormChange}
                      rows={4}
                      className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg focus:ring-2 focus:ring-[rgb(239,171,10)] focus:border-transparent text-white"
                      placeholder="Your message here..."
                      required
                      disabled={formStatus === 'sending'}
                    ></textarea>
                  </div>
                  
                  {formStatus === 'success' && (
                    <div className="p-4 bg-green-500/20 border border-green-500 rounded-lg text-green-300">
                      ✓ Message sent successfully! I'll get back to you soon.
                    </div>
                  )}
                  
                  {formStatus === 'error' && (
                    <div className="p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-300">
                      ✗ Failed to send message. Please try again or email me directly at thumma.vijayas@gmail.com
                    </div>
                  )}
                  
                  <button
                    type="submit"
                    disabled={formStatus === 'sending'}
                    className="w-full bg-gradient-to-r from-[rgb(239,171,10)] to-[rgb(255,200,50)] hover:from-[rgb(210,150,10)] hover:to-[rgb(230,180,40)] text-gray-900 font-medium py-3 px-6 rounded-lg transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none shadow-md hover:shadow-lg"
                  >
                    {formStatus === 'sending' ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-8 border-t border-slate-800">
        <div className="container mx-auto px-4 text-center">
          <p className="text-slate-400">
            © {new Date().getFullYear()} Vijay Sagar. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <Router>
      <Suspense fallback={<div className="min-h-screen bg-[#020617]" />}>
        <Routes>
          <Route path="/" element={<MainContent />} />
          <Route path="/photography" element={<Photography />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/designing" element={<Designing />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogDetail />} />
        </Routes>
      </Suspense>
      <Suspense fallback={null}>
        <MyraWidget />
      </Suspense>
    </Router>
  );
}

export default App;
